import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiArrowLeft, FiPlay, FiFileText, FiVideo, FiCheckCircle, FiHelpCircle, FiUsers, FiChevronDown, FiChevronUp, FiAward, FiFile, FiDownload } from 'react-icons/fi';
import CertificateModal from './CertificateModal';

import { API_URL, getAssetUrl } from '../config';

// Convert YouTube URLs to embed format
const convertToEmbedUrl = (url) => {
  if (!url) return url;
  
  // If already in embed format, return as is
  if (url.includes('/embed/')) {
    return url;
  }
  
  // Extract video ID from various YouTube URL formats
  let videoId = null;
  
  // Standard YouTube watch URL: https://www.youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (watchMatch) {
    videoId = watchMatch[1];
  }
  
  // YouTube short URL: https://youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([^&\n?#]+)/);
  if (shortMatch) {
    videoId = shortMatch[1];
  }
  
  // If we found a video ID, convert to embed format
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // If no match, return original URL (might be Vimeo or other service)
  return url;
};

function TrainingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [showCompletionStats, setShowCompletionStats] = useState(false);
  const [loadingCompletion, setLoadingCompletion] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [trainingCompletion, setTrainingCompletion] = useState(null);
  const [quizPassed, setQuizPassed] = useState(false);
  const [quizPendingReview, setQuizPendingReview] = useState(false);

  useEffect(() => {
    loadTraining();
    loadTrainingCompletion();
  }, [id]);

  const loadTraining = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings/${id}`);
      if (response.data.training) {
        setTraining(response.data.training);
        setQuizPassed(response.data.training.quizPassed || false);
        setQuizPendingReview(response.data.training.quizPendingReview || false);
        // Check if user has completed any modules
        const progressResponse = await axios.get(`${API_URL}/training/progress/my`);
        const progress = progressResponse.data.progress || {};
        const completed = new Set();
        response.data.training.modules.forEach(m => {
          if (progress[m.id]?.completed) {
            completed.add(m.id);
          }
        });
        setCompletedModules(completed);
      }
    } catch (error) {
      console.error('Error loading training:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrainingCompletion = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings/completions/my`);
      const completions = response.data.completions || {};
      if (completions[parseInt(id)]) {
        setTrainingCompletion(completions[parseInt(id)]);
      }
    } catch (error) {
      console.error('Error loading training completion:', error);
    }
  };

  const handleModuleClick = (moduleId) => {
    navigate(`/training/${id}/module/${moduleId}`);
  };

  const handleNextModule = () => {
    if (training && currentModuleIndex < training.modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
    } else if (training?.quiz && training.modules.every(m => completedModules.has(m.id))) {
      navigate(`/training/${id}/quiz`);
    }
  };

  const handlePreviousModule = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
      setShowQuiz(false);
    }
  };

  const markModuleComplete = async (moduleId) => {
    try {
      const response = await axios.post(`${API_URL}/training/${moduleId}/complete`);
      setCompletedModules(prev => new Set([...prev, moduleId]));
      
      // Check if the entire training was just completed
      if (response.data.trainingCompleted && response.data.completion) {
        setTrainingCompletion(response.data.completion);
        setShowCertificate(true);
      }
    } catch (error) {
      console.error('Error marking module complete:', error);
    }
  };

  const unmarkModuleComplete = async (moduleId) => {
    try {
      const userId = user?.id || user?.userId;
      console.log(`Unmarking module ${moduleId} for user ${userId}`, { user });
      if (!userId) {
        alert('Error: User ID not found. Please refresh the page and try again.');
        return;
      }
      const response = await axios.post(`${API_URL}/training/${moduleId}/uncomplete`, {
        userId: userId
      });
      console.log('Unmark response:', response.data);
      setCompletedModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleId);
        return newSet;
      });
      // Refresh completion data if it's loaded
      if (showCompletionStats && completionData) {
        loadCompletionData();
      }
      // Reload training to refresh progress
      loadTraining();
    } catch (error) {
      console.error('Error unmarking module complete:', error);
      console.error('Error response:', error.response?.data);
      alert(`Error unmarking module: ${error.response?.data?.error || error.message}`);
    }
  };

  const loadCompletionData = async () => {
    if (!user || user.role !== 'admin') return;
    
    setLoadingCompletion(true);
    try {
      const response = await axios.get(`${API_URL}/trainings/${id}/completion`);
      setCompletionData(response.data);
    } catch (error) {
      console.error('Error loading completion data:', error);
    } finally {
      setLoadingCompletion(false);
    }
  };

  const toggleCompletionStats = () => {
    if (!showCompletionStats && !completionData) {
      loadCompletionData();
    }
    setShowCompletionStats(!showCompletionStats);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-600">Loading training...</div>
        </div>
      </div>
    );
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Training not found</h2>
          <button
            onClick={() => navigate('/training')}
            className="text-[#3c79bb] hover:text-primary-700"
          >
            Back to Trainings
          </button>
        </div>
      </div>
    );
  }


  const currentModule = training.modules[currentModuleIndex];
  const progress = training.modules.length > 0 ? ((currentModuleIndex + 1) / training.modules.length) * 100 : 0;
  const allModulesComplete = training.modules.length > 0 && training.modules.every(m => completedModules.has(m.id));

  return (
    <div className="pb-8">
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate('/training')}
              className="p-2 hover:bg-white/50 rounded-xl transition-colors"
            >
              <FiArrowLeft className="text-xl text-gray-900" />
            </button>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{training.title}</h1>
          </div>
          {training.description && (
            <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium ml-12">{training.description}</p>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Training Completed Banner */}
        {trainingCompletion && (
          <div className="mb-6 glass-card rounded-2xl p-5 sm:p-6 border-2 border-green-300 bg-green-50/50">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FiAward className="text-2xl text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-900">Training Completed!</h3>
                  <p className="text-sm text-green-700 font-medium">
                    Completed on {new Date(trainingCompletion.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    {' • '}Certificate ID: {trainingCompletion.certificate_id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCertificate(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-semibold text-sm shadow-lg shadow-green-600/30"
              >
                <FiAward className="text-base" />
                View Certificate
              </button>
            </div>
          </div>
        )}

        {/* Completion Stats (Admin Only) */}
        {user?.role === 'admin' && (
          <div className="mb-6 glass-card rounded-2xl overflow-hidden">
            <button
              onClick={toggleCompletionStats}
              className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-white/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FiUsers className="text-xl text-[#3c79bb]" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Completion Statistics</h2>
              </div>
              {showCompletionStats ? (
                <FiChevronUp className="text-xl text-gray-700" />
              ) : (
                <FiChevronDown className="text-xl text-gray-700" />
              )}
            </button>
            
            {showCompletionStats && (
              <div className="p-4 sm:p-5 border-t border-white/20">
                {loadingCompletion ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3c79bb]"></div>
                    <span className="ml-3 text-gray-700">Loading completion data...</span>
                  </div>
                ) : completionData ? (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="glass-card rounded-xl p-4 text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-[#3c79bb]">
                          {completionData.completion.length}
                        </div>
                        <div className="text-sm text-gray-700 font-medium mt-1">Total Users</div>
                      </div>
                      <div className="glass-card rounded-xl p-4 text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-green-600">
                          {completionData.completion.filter(c => c.fullyCompleted).length}
                        </div>
                        <div className="text-sm text-gray-700 font-medium mt-1">Fully Completed</div>
                      </div>
                      <div className="glass-card rounded-xl p-4 text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-orange-600">
                          {completionData.completion.filter(c => !c.fullyCompleted && c.completedCount > 0).length}
                        </div>
                        <div className="text-sm text-gray-700 font-medium mt-1">In Progress</div>
                      </div>
                    </div>

                    {/* User Completion Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/20">
                            <th className="text-left py-3 px-4 font-bold text-gray-900">User</th>
                            {completionData.modules.map(module => (
                              <th key={module.id} className="text-center py-3 px-2 font-semibold text-gray-800 text-sm">
                                {module.title}
                              </th>
                            ))}
                            <th className="text-center py-3 px-4 font-bold text-gray-900">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {completionData.completion.map((userData) => (
                            <tr key={userData.user.id} className="border-b border-white/10 hover:bg-white/10">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  {userData.user.profileImage ? (
                                    <img
                                      src={getAssetUrl(userData.user.profileImage)}
                                      alt={userData.user.firstName || userData.user.username}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white font-semibold text-sm">
                                      {(userData.user.firstName?.[0] || userData.user.username?.[0] || 'U').toUpperCase()}
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {userData.user.firstName && userData.user.lastName
                                        ? `${userData.user.firstName} ${userData.user.lastName}`
                                        : userData.user.username}
                                    </div>
                                    <div className="text-xs text-gray-600">{userData.user.email}</div>
                                  </div>
                                </div>
                              </td>
                              {completionData.modules.map(module => (
                                <td key={module.id} className="text-center py-3 px-2">
                                  {userData.modules[module.id]?.completed ? (
                                    <FiCheckCircle className="text-green-500 text-xl mx-auto" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 mx-auto"></div>
                                  )}
                                </td>
                              ))}
                              <td className="text-center py-3 px-4">
                                <div className="flex flex-col items-center">
                                  <span className="font-bold text-gray-900 text-sm">
                                    {userData.completedCount}/{userData.totalModules}
                                  </span>
                                  <div className="w-20 h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-[#3c79bb] to-[#4ca86b] transition-all"
                                      style={{ width: `${userData.percentage}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-gray-600 mt-1">{userData.percentage}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600">No completion data available</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-6 glass-card rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm sm:text-base font-bold text-gray-900">
              Module {currentModuleIndex + 1} of {training.modules.length}
            </span>
            <span className="text-sm sm:text-base font-bold text-[#3c79bb]">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-4 shadow-inner border border-gray-400/30">
            <div
              className="bg-gradient-to-r from-[#3c79bb] to-[#4ca86b] h-4 rounded-full transition-all duration-300 shadow-md"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Quiz submitted, awaiting manual grading on written answers */}
        {training.quiz && !quizPassed && quizPendingReview && !trainingCompletion &&
          training.modules.length > 0 &&
          training.modules.every((m) => completedModules.has(m.id)) && (
            <div className="mb-6 glass-card rounded-2xl p-5 sm:p-6 border-2 border-violet-300 bg-violet-50/50">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                    <FiHelpCircle className="text-2xl text-violet-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-violet-900">Quiz submitted — awaiting review</h3>
                    <p className="text-sm text-violet-800 font-medium">
                      An administrator is grading your written responses. Check back later for your final
                      result.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/training/${id}/quiz`)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors font-semibold text-sm shadow-lg shadow-violet-600/30"
                >
                  <FiHelpCircle className="text-base" />
                  View status
                </button>
              </div>
            </div>
          )}

        {/* Quiz Required Banner - show when all modules done but quiz not passed */}
        {training.quiz && !quizPassed && !quizPendingReview && !trainingCompletion && 
         training.modules.length > 0 && 
         training.modules.every(m => completedModules.has(m.id)) && (
          <div className="mb-6 glass-card rounded-2xl p-5 sm:p-6 border-2 border-amber-300 bg-amber-50/50">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <FiHelpCircle className="text-2xl text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900">Quiz Required</h3>
                  <p className="text-sm text-amber-700 font-medium">
                    All modules are complete! Pass the quiz to earn your certificate.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/training/${id}/quiz`)}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-semibold text-sm shadow-lg shadow-amber-600/30"
              >
                <FiHelpCircle className="text-base" />
                Take Quiz
              </button>
            </div>
          </div>
        )}

        {/* Modules List Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="glass-card rounded-2xl p-4 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Modules</h3>
              <div className="space-y-2">
                {training.modules.map((module, index) => (
                  <button
                    key={module.id}
                    onClick={() => {
                      setCurrentModuleIndex(index);
                      setShowQuiz(false);
                    }}
                    className={`w-full text-left p-3 rounded-xl transition-all ${
                      index === currentModuleIndex
                        ? 'bg-[#3c79bb] text-white'
                        : 'bg-white/50 hover:bg-white/80 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {module.type === 'video' ? (
                        <FiVideo className="text-sm" />
                      ) : module.type === 'document' ? (
                        <FiFile className="text-sm" />
                      ) : (
                        <FiFileText className="text-sm" />
                      )}
                      <span className="text-sm font-medium truncate">{module.title}</span>
                      {completedModules.has(module.id) && (
                        <FiCheckCircle className="ml-auto text-green-500" />
                      )}
                    </div>
                  </button>
                ))}
                {training.quiz && (
                  <button
                    onClick={() => allModulesComplete ? navigate(`/training/${id}/quiz`) : null}
                    disabled={!allModulesComplete}
                    className={`w-full text-left p-3 rounded-xl transition-all ${
                      !allModulesComplete
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                        : quizPassed
                        ? 'bg-green-50 border border-green-200 text-gray-900'
                        : quizPendingReview
                        ? 'bg-violet-50 border border-violet-200 text-gray-900'
                        : 'bg-white/50 hover:bg-white/80 text-gray-900'
                    }`}
                    title={!allModulesComplete ? 'Complete all modules first' : ''}
                  >
                    <div className="flex items-center gap-2">
                      <FiHelpCircle className="text-sm" />
                      <span className="text-sm font-medium">Quiz</span>
                      {!allModulesComplete && (
                        <span className="ml-auto text-xs text-gray-400">🔒</span>
                      )}
                      {allModulesComplete && quizPendingReview && !quizPassed && (
                        <span className="ml-auto text-xs font-semibold text-violet-700">Review</span>
                      )}
                      {allModulesComplete && quizPassed && (
                        <FiCheckCircle className="ml-auto text-green-500" />
                      )}
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Current Module Content */}
          <div className="lg:col-span-3">
            {currentModule ? (
              <div className="glass-card rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    {currentModule.type === 'video' ? (
                      <FiVideo className="text-2xl text-[#3c79bb]" />
                    ) : currentModule.type === 'document' ? (
                      <FiFile className="text-2xl text-[#3c79bb]" />
                    ) : (
                      <FiFileText className="text-2xl text-[#3c79bb]" />
                    )}
                    <h2 className="text-2xl font-bold text-gray-900">{currentModule.title}</h2>
                    {currentModule.type === 'video' && currentModule.video_source === 'upload' && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">Uploaded</span>
                    )}
                    {currentModule.type === 'document' && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">Document</span>
                    )}
                  </div>
                {currentModule.description && (
                  <p className="text-gray-700 mb-6 font-medium">{currentModule.description}</p>
                )}

                {currentModule.type === 'text' ? (
                  <div className="prose max-w-none mb-6">
                    <div className="whitespace-pre-wrap text-gray-900 leading-relaxed font-medium">
                      {currentModule.content}
                    </div>
                  </div>
                ) : currentModule.type === 'video' ? (
                  <div className="mb-6">
                    <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                      {currentModule.video_source === 'upload' ? (
                        <video
                          src={getAssetUrl(currentModule.video_url)}
                          controls
                          className="w-full h-full"
                          title={currentModule.title}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <iframe
                          src={convertToEmbedUrl(currentModule.video_url)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={currentModule.title}
                          frameBorder="0"
                        ></iframe>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-6">
                    <div className="p-5 bg-white/60 rounded-xl border border-white/50">
                      <p className="text-gray-900 font-semibold mb-2">
                        {currentModule.file_name || 'Training document'}
                      </p>
                      <p className="text-sm text-gray-700 mb-4">
                        Download this training file to review the module content.
                      </p>
                      <a
                        href={getAssetUrl(currentModule.file_url)}
                        download={currentModule.file_name || true}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#3c79bb] text-white font-semibold hover:bg-[#306196] transition-colors"
                      >
                        <FiDownload className="text-base" />
                        Download File
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-6 border-t border-white/20">
                  <button
                    onClick={handlePreviousModule}
                    disabled={currentModuleIndex === 0}
                    className="px-4 py-2 rounded-xl bg-gray-100 text-gray-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex gap-3">
                    {!completedModules.has(currentModule.id) ? (
                      <button
                        onClick={() => markModuleComplete(currentModule.id)}
                        className="px-4 py-2 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors"
                      >
                        Mark Complete
                      </button>
                    ) : user?.role === 'admin' ? (
                      <button
                        onClick={() => unmarkModuleComplete(currentModule.id)}
                        className="px-4 py-2 rounded-xl bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors"
                      >
                        Unmark Complete
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600 font-semibold">
                        <FiCheckCircle className="text-xl" />
                        <span>Completed</span>
                      </div>
                    )}
                    <button
                      onClick={handleNextModule}
                      disabled={
                        currentModuleIndex === training.modules.length - 1 && 
                        (!training.quiz || (training.quiz && !allModulesComplete))
                      }
                      className="px-4 py-2 rounded-xl bg-[#3c79bb] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#306196] transition-colors"
                    >
                      {currentModuleIndex === training.modules.length - 1 && training.quiz
                        ? allModulesComplete ? 'Take Quiz' : 'Take Quiz 🔒'
                        : 'Next'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="glass-card rounded-2xl p-8 text-center">
                <p className="text-gray-700">No modules in this training yet.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Certificate Modal */}
      {showCertificate && trainingCompletion && (
        <CertificateModal
          completion={trainingCompletion}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
}

export default TrainingDetail;
